import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { Responsive, WidthProvider } from 'react-grid-layout';
import { FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';

import { PieChart, TreemapChart } from '../components';
import { NoData } from '../components/no-data';
import { WidgetNivo } from '../components/widget-nivo';
import { useDashboardStore } from '../dashboard-store';
import { MainDashboard as MainDashboardType, NivoMainDashboardType } from '../types';
import { convertBackendDataToNivo, shortenNumber } from '../utils';

const ResponsiveGridLayout = WidthProvider(Responsive);

const breakpoints = { xxl: 1536, xl: 1280, lg: 1024, md: 768 };

const cols = { xxl: 12, xl: 12, lg: 12, md: 12 };

interface Props {
  data: MainDashboardType | null | undefined;
}

export const MainDashboard = ({ data }: Props) => {
  const isResizable = useDashboardStore((state) => state.isResizable);
  
  if (!data) {
    return <div className="text-deep-gray-400">No main dashboard data.</div>;
  }
  
  const nivoData = convertBackendDataToNivo(
    data,
  ) as unknown as NivoMainDashboardType;

  const {
    tokensTotal,
    tokensLicense,
    errorsInTokenCounts,
    tokensUnsupported,
    missingUnsupportedTokens,
    sourceFilesTotal,
    executablesByType,
    tokensByWorkloadTypes,
  } = nivoData ?? {};

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {!data ? (
        <NoData />
      ) : (
        <>
          <div className="flex  w-full flex-col items-start">
            <h1 className=" py-4 pt-2 text-3xl font-medium text-slate-300">
              Project Overview
            </h1>
            <ResponsiveGridLayout
              className="w-full "
              breakpoints={breakpoints}
              cols={cols}
            >
              <div
                className="grid-cell"
                key="0"
                data-grid={{
                  w: 3,
                  h: 1,
                  x: 0,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Tokens"
                  secondaryText={shortenNumber(tokensTotal, 4)}
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                className="grid-cell"
                key="1"
                data-grid={{
                  w: 3,
                  h: 1,
                  x: 3,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="License Tokens"
                  secondaryText={shortenNumber(tokensLicense, 4)}
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                    secondaryText: errorsInTokenCounts
                      ? 'italic'
                      : 'not-italic',
                  }}
                ></WidgetNivo>
              </div>

              <div
                key="2"
                data-grid={{
                  w: 3,
                  h: 1,
                  x: 6,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Source Files"
                  secondaryText={sourceFilesTotal}
                  classNames={{
                    root: 'h-full ',
                    header: 'p-2',
                    content:
                      'my-auto justify-center items-center overflow-y-hidden',
                  }}
                ></WidgetNivo>
              </div>
              <div
                key="3"
                data-grid={{
                  w: 3,
                  h: 1,
                  x: 9,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Flagged Tokens"
                  secondaryText={
                    tokensUnsupported
                      ? shortenNumber(tokensUnsupported, 2)
                      : '0'
                  }
                  classNames={{
                    root: 'h-full ',
                    header: 'p-2',
                    content:
                      'my-auto justify-center items-center overflow-y-hidden',
                    secondaryText: missingUnsupportedTokens
                      ? 'italic'
                      : 'not-italic',
                  }}
                ></WidgetNivo>
              </div>
              <div
                key="4"
                data-grid={{
                  w: 6,
                  h: 3,
                  x: 0,
                  y: 1,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Executable Type"
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-80 overflow-hidden">
                    <PieChart
                      data={executablesByType}
                      sortByValue={true}
                      enableArcLinkLabels={true}
                      enableArcLabels={false}
                      centeredMetric={null}
                      legends={[
                        {
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 32,
                          translateY: 0,
                          itemsSpacing: 0,
                          itemWidth: 120,
                          itemHeight: 32,
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 12,
                          symbolShape: 'circle',
                        },
                      ]}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="5"
                data-grid={{
                  w: 6,
                  h: 3,
                  x: 6,
                  y: 1,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Workload Type"
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className=" h-64 overflow-hidden">
                    <TreemapChart data={tokensByWorkloadTypes}></TreemapChart>
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="6"
                data-grid={{
                  w: 12,
                  h: 0.5,
                  x: 0,
                  y: 4,
                  minW: 2,
                  minH: 0.5,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  classNames={{
                    root: 'h-full ',
                    content:
                      'my-auto justify-center overflow-hidden items-center ',
                  }}
                >
                  <div className="flex h-full w-full flex-row justify-center overflow-hidden p-2">
                    <div className="flex flex-row gap-8 overflow-hidden ">
                      <h3 className="font-medium">Notifications</h3>
                      <div className="flex items-center gap-2 text-center text-sm uppercase text-slate-300">
                        <FiInfo size={20} className="text-blue-500" /> Info:{' '}
                        {shortenNumber(data?.notifications?.INFO, 2)}
                      </div>
                      <div className="flex items-center gap-2 text-center text-sm uppercase text-slate-300">
                        {' '}
                        <FiAlertTriangle
                          size={20}
                          className="text-amber-500"
                        />{' '}
                        Warnings:{' '}
                        {shortenNumber(data?.notifications?.WARNING, 2)}
                      </div>
                      <div className="flex items-center gap-2 text-center text-sm uppercase text-slate-300">
                        <FiX size={20} className="text-rose-500" /> Errors:{' '}
                        {shortenNumber(data?.notifications?.ERROR, 2)}
                      </div>
                    </div>
                  </div>
                </WidgetNivo>
              </div>
            </ResponsiveGridLayout>
          </div>
        </>
      )}
    </div>
  );
};